import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./Notes.module.css";
import axios from "axios";
import { toast } from "react-toastify";
import { ethers } from "ethers";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/PageHeader";
import Loader from "../components/common/Loader";
import LoanList from "../components/loans/LoanList";
import NoteList from "../components/notes/NoteList";
import NoteDetails from "../components/notes/NoteDetails";
import HistoryList from "../components/notes/HistoryList";
import ButtonGroup from "../components/common/ButtonGroup";
import SearchInput from "../components/common/SearchInput";
import { TableSkeleton } from "../components/common/Skeleton";
import EmptyState from "../components/common/EmptyState";

const PORT = process.env.REACT_APP_PORT;

const Notes = () => {
  const { role, userInfo, magic } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkApplied = useRef(false);
  const [loanData, setLoanData] = useState([]);
  const [noteData, setNoteData] = useState([]);
  const [userData, setUserData] = useState([]);
  const [showLoanTable, setShowLoanTable] = useState(true);
  const [showNoteTable, setShowNoteTable] = useState(false);
  const [showHistoryTable, setShowHistoryTable] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isLoadingLoans, setIsLoadingLoans] = useState(false);
  const [isLoadingNotes, setIsLoadingNotes] = useState(false);
  const [currentLoanPage, setCurrentLoanPage] = useState(1);
  const [loanSort, setLoanSort] = useState({ sortBy: "loanId", order: "desc" });
  const [searchTerm, setSearchTerm] = useState("");
  // Client-side memo of loan pages keyed by `${page}|${sortBy}|${order}`.
  // Back-nav across pages or sort flips you've already seen = instant; we
  // still revalidate in the background if the entry is older than TTL.
  const loanPageCacheRef = useRef(new Map());
  const LOAN_PAGE_CACHE_TTL_MS = 60 * 1000;
  const LOAN_PAGE_CACHE_MAX = 50; // ~50 page-snapshots; bounds memory at scale

  // Debounced search term — drives the server query for admin (paginated)
  // and the client filter for user (full array). Debounce prevents a fetch
  // per keystroke at scale.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Precompute a lowercased search blob per item ONCE per data change.
  // Previously: JSON.stringify(item).toLowerCase() ran per item per keystroke
  // per render — O(n) string work on the main thread that stalled large lists.
  // Now: build once per fetch, filter is a cheap .includes lookup.
  const loanSearchIndex = useMemo(() => {
    if (!Array.isArray(loanData)) return null;
    return loanData.map((item) => JSON.stringify(item || "").toLowerCase());
  }, [loanData]);
  const noteSearchIndex = useMemo(() => {
    if (!Array.isArray(noteData)) return null;
    return noteData.map((item) => JSON.stringify(item || "").toLowerCase());
  }, [noteData]);

  // Admin loans are paginated server-side (filtering done on backend) — pass through.
  // User loans come back as a flat array — filter client-side and wrap into the
  // paginated shape LoanList expects ({ data, pagination }).
  const wrapAsPaginated = (arr) => ({
    data: arr,
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalItems: arr.length,
      hasNext: false,
      hasPrev: false,
    },
  });

  const filteredLoanData = useMemo(() => {
    if (!Array.isArray(loanData)) return loanData;
    const q = debouncedSearch.toLowerCase();
    if (!q) return wrapAsPaginated(loanData);
    const filtered = loanData.filter((_item, i) =>
      loanSearchIndex[i].includes(q)
    );
    return wrapAsPaginated(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanData, loanSearchIndex, debouncedSearch]);

  const filteredNoteData = useMemo(() => {
    if (!Array.isArray(noteData)) return noteData;
    const q = debouncedSearch.toLowerCase();
    if (!q) return noteData;
    return noteData.filter((_item, i) => noteSearchIndex[i].includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData, noteSearchIndex, debouncedSearch]);

  // Normalized "no loans" check that handles both shapes (object for admin,
  // array for user). The old `loanData.length === 0` only worked for arrays.
  const loanIsEmpty = Array.isArray(loanData)
    ? loanData.length === 0
    : !loanData || !Array.isArray(loanData.data) || loanData.data.length === 0;

  useEffect(() => {
    if (role === "admin") {
      fetchLoans(currentLoanPage, loanSort, debouncedSearch);
    } else if (userInfo?.email) {
      fetchUserLoans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, userInfo]);

  // When the admin search term changes (debounced), reset to page 1 and
  // refetch from the server with the new `q`.
  useEffect(() => {
    if (role !== "admin") return;
    setCurrentLoanPage(1);
    fetchLoans(1, loanSort, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const fetchLoans = async (page = 1, sort = loanSort, q = debouncedSearch) => {
    const key = `${page}|${sort.sortBy}|${sort.order}|${q}`;
    const cached = loanPageCacheRef.current.get(key);
    const now = Date.now();
    const fresh = cached && now - cached.ts < LOAN_PAGE_CACHE_TTL_MS;

    // Serve cache immediately if we have it; only show spinner on a cold miss.
    if (cached) {
      setLoanData(cached.data);
      if (fresh) return;
    } else {
      setIsLoadingLoans(true);
    }

    try {
      const qsParams = {
        page: String(page),
        sortBy: sort.sortBy,
        order: sort.order,
      };
      if (q) qsParams.q = q;
      const qs = new URLSearchParams(qsParams);
      const response = await axios.get(`${PORT}getAllLoans?${qs.toString()}`);
      const cache = loanPageCacheRef.current;
      if (cache.has(key)) cache.delete(key); // re-insert for LRU recency
      cache.set(key, { data: response.data, ts: Date.now() });
      while (cache.size > LOAN_PAGE_CACHE_MAX) {
        cache.delete(cache.keys().next().value); // evict oldest
      }
      setLoanData(response.data);
    } catch (error) {
      console.error("Error fetching loans:", error);
      if (!cached) toast.error("Failed to fetch loans");
    } finally {
      setIsLoadingLoans(false);
    }
  };

  const handleLoanSortChange = (sortBy) => {
    // Cycle: same col -> flip dir. New col -> default desc.
    const next =
      loanSort.sortBy === sortBy
        ? { sortBy, order: loanSort.order === "asc" ? "desc" : "asc" }
        : { sortBy, order: "desc" };
    setLoanSort(next);
    setCurrentLoanPage(1);
    if (role === "admin") fetchLoans(1, next, debouncedSearch);
  };

  const fetchUserLoans = async () => {
    try {
      setIsLoadingLoans(true);
      // We need to get the user's wallet address from userInfo or magic
      // In App.js it was fetching user info from backend then calling fetchOwnerDetails
      // Here we assume userInfo from context has publicAddress
      
      if (!userInfo?.publicAddress) return;

      const address = userInfo.publicAddress;
      const result = await axios.post(`${PORT}getOwnerLoanAndNotes`, { address });
      const notes = result.data;

      const formattedResult = notes.map((item) => {
        if (Array.isArray(item)) {
          return item.map((subItem) => {
            if (subItem._hex) {
              return ethers.BigNumber.from(subItem).toNumber();
            }
            return subItem;
          });
        } else if (item._hex) {
          return ethers.BigNumber.from(item).toNumber();
        }
        return item;
      });

      const sortedData = formattedResult.sort((a, b) => a.id - b.id);
      setUserData(sortedData);
      
      const loanIds = [...new Set(sortedData.map((item) => item[0]))];
      if (loanIds.length > 0) {
        const response = await axios.post(`${PORT}getMyLoans`, { loanIds });
        setLoanData(response.data);
      } else {
        setLoanData([]);
      }
    } catch (error) {
      console.error("Error fetching user loans:", error);
      toast.error("Failed to fetch your loans");
    } finally {
      setIsLoadingLoans(false);
    }
  };

  const fetchNotes = async (loanId) => {
    try {
      setIsLoadingNotes(true);
      const response = await axios.post(`${PORT}getAllNotes`, { loanId });
      setNoteData(response.data);
    } catch (error) {
      console.error("Error fetching notes:", error);
      toast.error("Failed to fetch notes");
    } finally {
      setIsLoadingNotes(false);
    }
  };

  const fetchMyNotes = async (loanId) => {
    try {
      setIsLoadingNotes(true);
      const response = await axios.post(`${PORT}getMyNotes`, { loanId, userData });
      setNoteData(response.data);
    } catch (error) {
      console.error("Error fetching my notes:", error);
      toast.error("Failed to fetch your notes");
    } finally {
      setIsLoadingNotes(false);
    }
  };

  // Deep-link from FlowDrilldown — `/notes?loanId=X[&noteId=Y]`
  // Wait until loanData has arrived (so we can confirm the loan is in scope),
  // then drive the same UI flow the user would manually.
  useEffect(() => {
    if (deepLinkApplied.current) return;
    const loanIdParam = searchParams.get("loanId");
    const noteIdParam = searchParams.get("noteId");
    if (!loanIdParam) return;
    if (isLoadingLoans) return;

    deepLinkApplied.current = true;
    const loanIdNum = parseInt(loanIdParam, 10);
    handleLoanSelection(loanIdNum);

    if (noteIdParam) {
      // Once notes have loaded, pick the matching one. We can't wait inline,
      // so we set a queued effect via state.
      setPendingNoteId(parseInt(noteIdParam, 10));
    }
    // Clean the URL — keep the page bookmarkable but tidy.
    const next = new URLSearchParams(searchParams);
    next.delete("loanId");
    next.delete("noteId");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingLoans]);

  const [pendingNoteId, setPendingNoteId] = useState(null);

  // Once noteData is fetched, if we have a pending noteId, auto-select it.
  useEffect(() => {
    if (pendingNoteId == null) return;
    if (!Array.isArray(noteData) || noteData.length === 0) return;
    const match = noteData.find((n) => {
      const id = n?.metadata?.id ?? n?.id ?? n?.noteId;
      return Number(id) === pendingNoteId;
    });
    if (match) handleNoteSelection(match);
    setPendingNoteId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteData, pendingNoteId]);

  const handleLoanSelection = (loanId) => {
    if (role === "admin") {
      fetchNotes(loanId);
    } else {
      fetchMyNotes(loanId);
    }
    setShowNoteTable(true);
    setShowLoanTable(false);
  };

  const handleNoteSelection = (note) => {
    setSelectedNote(note);
    setShowNoteTable(false);
  };

  const showHistory = async (selectedNote) => {
    try {
      const response = await axios.post(`${PORT}getNoteHistory`, { selectedNote });
      setShowHistoryTable(true);
      setHistoryData(response.data);
    } catch (error) {
      console.error("Error fetching note history:", error);
      toast.error("Failed to fetch note history");
    }
  };

  const removeLoanSelection = () => {
    setShowLoanTable(true);
    setShowNoteTable(false);
    setNoteData([]);
  };

  const removeNoteSelection = () => {
    setSelectedNote(null);
    setShowLoanTable(false);
    setShowNoteTable(true);
  };

  const removeHistorySelection = () => {
    setShowHistoryTable(false);
    setHistoryData([]);
  };

  return (
    <div>
      <PageHeader pageName="Notes" />
      <div className="main-page-content fade-in">
        <div className="page-toolbar">
          <h2 className="page-toolbar-title">
            {showLoanTable ? "Loans" : showNoteTable ? "Notes" : showHistoryTable ? "History" : "Note details"}
          </h2>
          <div className="page-toolbar-actions">
            {(showLoanTable || showNoteTable) && (
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                debounce={0}
                placeholder={showLoanTable ? "Search loans..." : "Search notes..."}
              />
            )}
          </div>
        </div>
        {isLoadingLoans ? (
          <TableSkeleton rows={6} cols={5} />
        ) : loanIsEmpty && !isLoadingLoans && !debouncedSearch ? (
          <EmptyState
            icon="bi-journal-x"
            title="No notes yet"
            subtitle="Once loans are originated and notes minted, they'll show up here."
          />
        ) : (
          <div>
            {showLoanTable ? (
              <LoanList
                loanData={filteredLoanData}
                handleLoanSelection={handleLoanSelection}
                currentPage={currentLoanPage}
                onPageChange={(page) => {
                  setCurrentLoanPage(page);
                  fetchLoans(page, loanSort, debouncedSearch);
                }}
                isLoading={isLoadingLoans}
                sortBy={loanSort.sortBy}
                order={loanSort.order}
                onSortChange={role === "admin" ? handleLoanSortChange : undefined}
              />
            ) : isLoadingNotes ? (
              <TableSkeleton rows={5} cols={6} />
            ) : !showNoteTable ? (
              showHistoryTable ? (
                <HistoryList historyData={historyData} />
              ) : (
                <NoteDetails selectedNote={selectedNote} />
              )
            ) : (
              <NoteList
                noteData={filteredNoteData}
                handleNoteSelection={handleNoteSelection}
                refreshData={() => role === "admin" ? fetchNotes(noteData[0]?.metadata?.loanId) : fetchMyNotes(noteData[0]?.metadata?.loanId)}
              />
            )}
            {!showLoanTable && (
              <ButtonGroup
                showNoteTable={showNoteTable}
                showHistoryTable={showHistoryTable}
                removeLoanSelection={removeLoanSelection}
                removeNoteSelection={removeNoteSelection}
                removeHistorySelection={removeHistorySelection}
                showHistory={showHistory}
                showLoanTable={showLoanTable}
                selectedNote={selectedNote}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Notes;
